import { Navigate, useLocation, useParams } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { getBranches } from '@/api/deployments';

interface CodeServerRouteProps {
  children: React.ReactNode;
}

/**
 * Route guard that allows access if the project has code_server_enabled on any build.
 * This is used for AI features that should be available to all users on enabled projects.
 */
export function CodeServerRoute({ children }: CodeServerRouteProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const location = useLocation();
  const { projectId } = useParams<{ projectId: string }>();

  const { data: branchesData, isLoading: branchesLoading } = useQuery({
    queryKey: ['branches', projectId],
    queryFn: () => getBranches(projectId!),
    enabled: !!projectId && isAuthenticated,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  if (authLoading || branchesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Check if any build has code_server_enabled
  const hasCodeServerEnabled = branchesData?.branches_with_builds?.some(
    branch => branch.latest_build?.code_server_enabled
  ) ?? false;

  if (!hasCodeServerEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
        <div className="bg-white border shadow-sm rounded-lg p-6 max-w-md text-center space-y-3">
          <div className="flex justify-center">
            <Lock className="h-10 w-10 text-amber-500" />
          </div>
          <h1 className="text-lg font-semibold">Feature not available</h1>
          <p className="text-sm text-muted-foreground">
            AI features are not enabled for this project. Contact support to enable them.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
